import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "mdp_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(KEY);
      if (stored) setItems(JSON.parse(stored));
    } catch {
      /* noop */
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const addItem = (product, options = {}) => {
    const mono = (options.monogram || "").toString().toUpperCase().slice(0, 3) || null;
    const key = `${product.id}-${options.size || ""}-${options.color || ""}-${mono || ""}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + (options.quantity || 1) } : i));
      }
      return [
        ...prev,
        {
          key,
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: (product.images || [])[0],
          size: options.size || null,
          color: options.color || null,
          quantity: options.quantity || 1,
          monogram: mono,
          monogram_price: Number(options.monogram_price || 0),
          category: product.category || null,
          processing_time: product.processing_time || null,
          local_pickup: !!product.local_pickup,
          preorder: !!product.preorder,
          preorder_note: product.preorder_note || null,
          preorder_date: product.preorder_date || null,
          preorder_fulfillment: product.preorder_fulfillment || "ship",
        },
      ];
    });
  };

  const updateQuantity = (key, qty) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, qty) } : i)));
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const total = useMemo(() => items.reduce((s, i) => s + (i.price + Number(i.monogram_price || 0)) * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, remove, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
